import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications, useUnreadCount, markAllAsRead } from '../../hooks/useNotifications';
import { groupNotificationsByDate } from '../../types/notifications';
import NotificationItem from './NotificationItem';
import { useNotification } from '../../contexts/NotificationContext';

interface NotificationCenterProps {
  onClose?: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onClose }) => {
  const { colors } = useTheme();
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [showRead, setShowRead] = useState(true);
  const { notifications, isLoading, mutate } = useNotifications({
    type: filterType,
    read: showRead ? undefined : false,
    limit: 50,
  });
  const { count: unreadCount } = useUnreadCount();
  const { showError, showSuccess } = useNotification();

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      mutate();
      showSuccess('All notifications marked as read');
    } catch (error: any) {
      showError(error?.message || 'Failed to mark all notifications as read');
    }
  };

  const handleMarkAsRead = (id: number) => {
    mutate();
  };

  const handleDelete = (id: number) => {
    mutate();
  };

  const groupedNotifications = groupNotificationsByDate(notifications);

  return (
    <div
      className={`${colors.surface} rounded-lg shadow-lg border ${colors.border} max-h-[600px] flex flex-col`}
    >
      {/* Header */}
      <div className={`p-4 border-b ${colors.border} flex items-center justify-between`}>
        <h3 className={`text-lg font-semibold ${colors.text}`}>Notifications</h3>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Mark all as read
            </button>
          )}
          <button
            onClick={onClose}
            className={`${colors.textMuted} ${colors.hoverTextSecondary} transition-colors`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={`p-3 border-b ${colors.border} flex items-center gap-2`}>
        <select
          value={filterType || ''}
          onChange={(e) => setFilterType(e.target.value || undefined)}
          className={`flex-1 text-sm border ${colors.border} rounded-md px-2 py-1.5 ${colors.surface} ${colors.text} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
        >
          <option value="">All Types</option>
          <option value="appointment">Appointments</option>
          <option value="client">Organizations</option>
          <option value="todo">Todos</option>
          <option value="email_mention">Mentions</option>
        </select>
        <button
          onClick={() => setShowRead(!showRead)}
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors ${
            showRead
              ? `${colors.surfaceHover} ${colors.textSecondary} ${colors.border}`
              : 'bg-indigo-600 text-white border-indigo-600'
          }`}
        >
          {showRead ? 'All' : 'Unread Only'}
        </button>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className={`p-4 text-center ${colors.textMuted}`}>Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className={`p-4 text-center ${colors.textMuted}`}>No notifications</div>
        ) : (
          <>
            {groupedNotifications.today.length > 0 && (
              <div>
                <div className={`px-4 py-2 ${colors.surfaceHover} border-b ${colors.border}`}>
                  <h4 className={`text-xs font-semibold ${colors.textSecondary} uppercase`}>Today</h4>
                </div>
                {groupedNotifications.today.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}

            {groupedNotifications.yesterday.length > 0 && (
              <div>
                <div className={`px-4 py-2 ${colors.surfaceHover} border-b ${colors.border}`}>
                  <h4 className={`text-xs font-semibold ${colors.textSecondary} uppercase`}>Yesterday</h4>
                </div>
                {groupedNotifications.yesterday.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}

            {groupedNotifications.thisWeek.length > 0 && (
              <div>
                <div className={`px-4 py-2 ${colors.surfaceHover} border-b ${colors.border}`}>
                  <h4 className={`text-xs font-semibold ${colors.textSecondary} uppercase`}>This Week</h4>
                </div>
                {groupedNotifications.thisWeek.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}

            {groupedNotifications.older.length > 0 && (
              <div>
                <div className={`px-4 py-2 ${colors.surfaceHover} border-b ${colors.border}`}>
                  <h4 className={`text-xs font-semibold ${colors.textSecondary} uppercase`}>Older</h4>
                </div>
                {groupedNotifications.older.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;
